import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { ActionButtonVariants } from 'components/action-button';
import { Picture } from 'components/picture';
import { useFeaturesLoyaltyTiersContent } from 'state/loyalty/hooks/use-feature-loyalty-tiers-content';
import { ModalType, useLoyaltyUserTier } from 'state/loyalty/hooks/use-loyalty-user-tier';

import { SuccessAnimation } from '../loyalty-opt-in-modal/success-animation';

import { Block, ButtonContainer, CloseButton, ContinueButton, Modal } from './styled';

export interface ILoyaltyTiersModalProps {
  onDismiss: VoidFunction;
  onConfirm: VoidFunction;
  modalType: ModalType | null;
}

export const LoyaltyTiersModal: React.FC<ILoyaltyTiersModalProps> = ({
  onConfirm,
  onDismiss,
  modalType,
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { loyaltyTierKey: userTier } = useLoyaltyUserTier();
  const { loading, getContentForTier } = useFeaturesLoyaltyTiersContent();

  const tiersDetails = getContentForTier(userTier);

  const onContinue = useCallback(() => {
    onConfirm();
    if (tiersDetails?.loyaltyTiersModalMainBtnLink?.locale) {
      navigate(tiersDetails?.loyaltyTiersModalMainBtnLink?.locale);
    }
  }, [navigate, onConfirm, tiersDetails?.loyaltyTiersModalMainBtnLink?.locale]);

  if (loading) {
    return null;
  }

  return (
    <Modal
      className="loyalty-tiers-modal"
      heading={tiersDetails?.loyaltyTiersModalTitle?.locale}
      body={<Block content={tiersDetails?.loyaltyTiersModalDescription?.localeRaw} />}
      image={<Picture image={tiersDetails?.loyaltyTiersModalImage} alt="" objectFitContain />}
      onDismiss={onDismiss}
      actions={
        <ButtonContainer>
          {modalType === ModalType.UPGRADE && <SuccessAnimation />}
          <ContinueButton onlyIcon fullWidth onClick={onContinue} data-testid="dialog-confirm-btn">
            {tiersDetails?.loyaltyTiersModalMainBtnLabel?.locale}
          </ContinueButton>
          <CloseButton
            variant={ActionButtonVariants.TEXT_ONLY}
            fullWidth
            onClick={onDismiss}
            data-testid="dialog-close-btn"
          >
            {formatMessage({ id: 'close' })}
          </CloseButton>
        </ButtonContainer>
      }
      modalAppearanceEventMessage="Confirmation Modal: Loyalty Tier Changed"
    />
  );
};
